import { createRef } from 'react';
import { reviewActionOr, reviewChangesOr, updateElementAudit } from './audit';
import { collectionChanges } from './change-api';
import { getAutoCompleteInput, inputValueSelection } from './controls';
import { Audit, DownCode, Provider } from './domain';
import {
  deleteDowncodesRequest,
  saveDowncodesRequest,
  updateDowncodeRequest,
} from './downcodes';
import { FormApi, formApi, providerField } from './form';
import { listApi } from './list';
import { WorksheetContext } from './visit-context';
import { isReviewWorkflowState } from './workflow-api';
import { WorksheetFormApi } from './worksheet-form-api';

const defaultEmCodes = [
  { code: 99285 },
  { code: 99284 },
  { code: 99283 },
  { code: 99282 },
  { code: 99281 },
  { code: 99291 },
];

export interface DownCodeApi extends WorksheetFormApi<DownCode> {}

export function downCodeApi(context: WorksheetContext): DownCodeApi {
  const { worksheet, status, config, downCodes: downcodes, readonly } = context;
  const { worksheetId, owningProvider } = worksheet;
  const { providers } = config;

  const emCodes = (config.emCodes || (defaultEmCodes as any)).map((v) =>
    v.code.toString()
  );

  const downCodeReasons = config.downcodeList;

  const defaultFilter = isReviewWorkflowState() ? 'all' : 'active';
  const list = listApi({
    defaultFilter,
    all: downcodes,
    id: (a: DownCode) => a.actualCPTCode,
    equals: (a, b) => a.actualCPTCode === b.actualCPTCode,
    active: (v) => v.active,
    setActive: (v) => (v.active = true),
    setInactive: (v) => (v.active = false),
  });

  let editing: DownCode;
  function editItem(v: DownCode) {
    api.onEdit();
    list.actions.edit(v);

    editing = v;

    form.setValues({
      targetCPTCode: targetCPTCode.setListValue((c) => c === v.targetCPTCode),
      actualCPTCode: actualCPTCode.setListValue((c) => c === v.actualCPTCode),
      reason: reason.setListValue((c) => c.description === v.reason),
      provider: provider.setListValue(v.provider),
      comment: v.comment,
    });
  }

  function addItem(v: DownCode) {
    async function request(update?: (v: any) => void) {
      if (update) {
        update(v);
      }
      v.downcodeId = editing?.downcodeId;

      const response = await (editing
        ? updateDowncodeRequest(v)
        : saveDowncodesRequest(v));
      if (response.success) {
        const item = response.result;

        if (update) {
          update(item);
        }

        if (editing) {
          list.actions.replace(
            item,
            (v) => v.downcodeId === editing.downcodeId
          );
        } else {
          list.actions.add(item);
        }

        focusInput('');
      } else {
        setStatus((response.errors || []).join(' '));
      }

      editing = null;
    }

    const edit = list.data.getState().editing;
    if (edit) {
      reviewChangesOr(
        {
          title: 'Review Changes For Down Code ',
          original: edit,
          update: v,
          changeReasons: {
            actualCPTCode: config.auditDownCode,
            targetCPTCode: config.auditDownCode,
          },
          fieldNames: ['provider', 'actualCPTCode', 'targetCPTCode'],
          onComplete: (a: Audit) => {
            request((v) => updateElementAudit(v, a));
          },
          onCancel: () => (editing = null),
        },
        request
      );
    } else {
      reviewActionOr(
        {
          title: 'Add Downcode ',
          list: config.auditDownCode,
          onComplete: (a) => {
            a.action = 'CREATE';
            request((v) => updateElementAudit(v, a));
          },
          onCancel: () => (editing = null),
        },
        request
      );
    }
  }

  function removeItem(v: DownCode) {
    async function request() {
      const response = await deleteDowncodesRequest({
        worksheetId: worksheet.worksheetId,
        downcodeId: v.downcodeId,
      });
      if (response.success) {
        list.actions.remove(v);
      } else {
        setStatus((response.errors || []).join(' '));
      }
    }
    reviewActionOr(
      {
        title: 'Remove Downcode ',
        list: context.config.auditDownCode,
        onComplete: (a) => {
          a.action = 'DELETE';
          v.audit = a;
          request();
        },
      },
      request
    );
  }

  const initialValues = {
    targetCPTCode: null,
    actualCPTCode: null,
    reason: null,
    provider: owningProvider,
    comment: '',
  };

  const setStatus = status.getState().setStatus;

  const form: FormApi<Partial<DownCode>> = formApi({
    disabled: readonly,
    values: initialValues,
    fields: {
      targetCPTCode: {
        label: 'Target',
        list: emCodes,
        listLabel: (v) => v,
        required: true,
        inputRef: createRef(),
        onEnterValid: () => {
          form.submit();
        },
      },
      actualCPTCode: {
        label: 'Actual',
        list: emCodes,
        listLabel: (v) => v,
        required: true,
        onEnterValid: () => {
          form.submit();
        },
      },
      reason: {
        label: 'Reason',
        list: downCodeReasons,
        listLabel: (v) => v.description,
        required: true,
        onEnterValid: () => {
          form.submit();
        },
      },
      provider: providerField(providers, {
        onEnter: () => form.submit(),
        onEnterValid: () => {
          form.submit();
        },
      }),
      comment: {
        label: 'Comments',
        onEnter: () => form.submit(),
      },
    },
    valid: (v) => {
      return (
        v.targetCPTCode && v.actualCPTCode && v.reason && v.provider !== null
      );
    },
    onSubmit: async (v) => {
      addItem({
        worksheetId,
        targetCPTCode: v.targetCPTCode,
        actualCPTCode: v.actualCPTCode,
        reason: v.reason.description,
        provider: v.provider,
        comment: v.comment,
      });
    },
    resetValues: () => initialValues,
  });

  const { targetCPTCode, actualCPTCode, reason, provider } = form.controls;

  const inputElement = getAutoCompleteInput(targetCPTCode.inputRef);
  const focusInput = inputValueSelection(inputElement);

  const listChanges = collectionChanges(list, {
    changeLabel: (v: DownCode[]) => v.map((u) => u.actualCPTCode).join(', '),
  });

  function setPhysician(providerId: String) {
    const defaultProvider = providers.filter(
      (v: Provider) => v.providerId === providerId
    )[0];

    if (defaultProvider) {
      initialValues.provider = defaultProvider;
    }
    provider.setListValue((v: Provider) => v.providerId === providerId);
  }

  const api = {
    form,
    list,
    listChanges,
    context,
    addItem,
    editItem,
    removeItem,
    setPhysician,
    onEdit: () => null,
  };
  return api;
}
